// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Add "hideLoadingOverlay" code inside PreStyle/globalStyle
 */

import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import xLoadingOverlay from "@sc/App/Handlers/XNG/LoadingOverlay";
import { CustomAppState } from "@sc/App/Store/Reducers";
import React, { FC, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isErrorModalOpen: state.context.isErrorModalOpen,
        pages: state.data.pages,
        isLoadingOverlay: state.xCommon.loadingOverlay,
        isSigningIn: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
    };
};

const mapDispatchToProps = {
    xLoadingOverlay,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// Global component uses in Header

const XngLoadingOverlayUtilities: FC<Props> = ({
    isErrorModalOpen,
    pages,
    xLoadingOverlay,
    isLoadingOverlay,
    isSigningIn,
}) => {
    const isShowing = () => isSigningIn || (isLoadingOverlay && isLoadingOverlay?.length > 0);
    const [showLoading, setShowLoading] = useState(isShowing());

    useEffect(() => {
        const showXngLoading = isShowing();
        setShowLoading(showXngLoading);

        if (typeof document === "undefined") {
            return;
        }

        /** To hide all loadingOverlay and spinner
         * We added "hideLoadingOverlay" inside PreStyle/globalStyle
         */
        const element = document.getElementById("react-app");
        if (showXngLoading) {
            element?.classList.add(`remove-ootb-spinners`);
        } else {
            element?.classList.remove(`remove-ootb-spinners`);
        }
    }, [isSigningIn, isLoadingOverlay]);

    /** To close LoadingOverlay when the page redirect to another path */
    useEffect(() => {
        xLoadingOverlay({ key: "clear", id: "" });
    }, [pages.location]);

    /** To close LoadingOverlay when ErrorModal open */
    useEffect(() => {
        if (isErrorModalOpen) {
            xLoadingOverlay({ key: "clear", id: "" });
        }
    }, [isErrorModalOpen]);

    const LoadingElement = ({ showLoading }: { showLoading?: boolean }) => (
        <>
            {showLoading && (
                <LoadingOverlay id="xng-loading-overlay" data-test-selector="global-loadingOverlay" loading={true} />
            )}
        </>
    );

    if (IS_SERVER_SIDE) {
        return <LoadingElement showLoading={true} />;
    }

    return ReactDOM.createPortal(<LoadingElement showLoading={showLoading} />, document.body);
};

export default connect(mapStateToProps, mapDispatchToProps)(XngLoadingOverlayUtilities);
